import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Radar, TrendingUp, TrendingDown, Loader2, Send, X, Zap, Bot } from "lucide-react";

const EMPTY = { asset: "XAUUSD", direction: "BUY", entry: "", sl: "", tp1: "", tp2: "", tp3: "", note: "" };

export function MentorSignalComposer() {
  const [mode, setMode] = useState("auto");
  const [form, setForm] = useState(EMPTY);
  const [active, setActive] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, s] = await Promise.all([api.get("/admin/signal-mode"), api.get("/admin/mentor/signals")]);
      setMode(m.data.mode); setActive(s.data.active || []);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 12000); return () => clearInterval(t); }, [load]);

  const toggleMode = async (checked) => {
    const next = checked ? "mentor" : "auto";
    try { await api.post("/admin/signal-mode", { mode: next }); setMode(next);
      toast.success(next === "mentor" ? "Mòd Mentor aktive — scanner otomatik la kanpe" : "Mòd Otomatik aktive — scanner an ap travay"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const post = async () => {
    for (const k of ["entry", "sl", "tp1"]) if (!form[k]) { toast.error("Entry, SL ak TP1 obligatwa"); return; }
    setBusy(true);
    try {
      await api.post("/admin/mentor/signal", {
        asset: form.asset, direction: form.direction,
        entry: Number(form.entry), sl: Number(form.sl), tp1: Number(form.tp1),
        tp2: form.tp2 ? Number(form.tp2) : null, tp3: form.tp3 ? Number(form.tp3) : null,
        note: form.note || null,
      });
      toast.success("Signal poste — kliyan yo ap wè l"); setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const close = async (id, result) => {
    setBusy(true);
    try { await api.post(`/admin/mentor/signal/${id}/close`, { result }); toast.success(`Signal fèmen (${result})`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="mentor-composer">
      {/* Mode toggle */}
      <div className="border border-[#222] bg-[#0F0F0F] p-4 flex items-center justify-between max-w-2xl">
        <div className="flex items-center gap-3">
          <div className={`h-9 w-9 flex items-center justify-center rounded-sm ${mode === "mentor" ? "bg-[#00F593]" : "bg-[#1C8CFF]"}`}>
            {mode === "mentor" ? <Zap className="h-4 w-4 text-black" /> : <Bot className="h-4 w-4 text-white" />}
          </div>
          <div>
            <div className="font-heading font-semibold text-sm">Signal Mode: {mode === "mentor" ? "Mentor (manyèl)" : "Otomatik (scanner)"}</div>
            <div className="font-mono-term text-[11px] text-[#888]">{mode === "mentor" ? "Scanner an kanpe — se signal mentor yo ki parèt" : "Scanner 21-strateji a ap travay otomatikman"}</div>
          </div>
        </div>
        <Switch checked={mode === "mentor"} onCheckedChange={toggleMode} data-testid="signal-mode-switch" />
      </div>

      {/* Composer */}
      <div className="border border-[#222] bg-[#0F0F0F] p-5 max-w-2xl space-y-3">
        <div className="flex items-center gap-2"><Radar className="h-4 w-4 text-[#00F593]" />
          <span className="font-heading font-semibold">Poste yon Signal Gold</span></div>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => setForm({ ...form, direction: "BUY" })} data-testid="composer-buy"
            className={`h-10 rounded-sm border text-sm font-bold flex items-center justify-center gap-1.5 ${form.direction === "BUY" ? "border-[#22e07b] bg-[#22e07b]/10 text-[#22e07b]" : "border-[#222] text-[#888]"}`}><TrendingUp className="h-4 w-4" /> BUY</button>
          <button type="button" onClick={() => setForm({ ...form, direction: "SELL" })} data-testid="composer-sell"
            className={`h-10 rounded-sm border text-sm font-bold flex items-center justify-center gap-1.5 ${form.direction === "SELL" ? "border-[#ff5b5b] bg-[#ff5b5b]/10 text-[#ff5b5b]" : "border-[#222] text-[#888]"}`}><TrendingDown className="h-4 w-4" /> SELL</button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {[["entry", "Entry"], ["sl", "Stop Loss"], ["tp1", "TP1"], ["tp2", "TP2 (opsyonèl)"], ["tp3", "TP3 (opsyonèl)"]].map(([k, label]) => (
            <div key={k}>
              <label className="font-mono-term text-[10px] uppercase tracking-widest text-[#888]">{label}</label>
              <Input type="number" step="0.01" value={form[k]} data-testid={`composer-${k}`}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="mt-1 bg-[#0A0A0A] border-[#222] rounded-sm h-10" />
            </div>
          ))}
        </div>
        <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="composer-note"
          placeholder="Nòt (opsyonèl) — ex: CPI breakout" rows={2} className="bg-[#0A0A0A] border-[#222] rounded-sm" />
        <Button onClick={post} disabled={busy} data-testid="composer-post"
          className="h-11 rounded-sm bg-[#00F593] text-black hover:bg-[#00d97f] font-semibold px-6">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-1.5 h-4 w-4" /> Poste Signal</>}
        </Button>
      </div>

      {/* Active mentor signals */}
      <div className="max-w-2xl">
        <div className="font-mono-term text-xs uppercase tracking-widest text-[#888] mb-2">Signal Aktif ({active.length})</div>
        <div className="border border-[#222] bg-[#0F0F0F]" data-testid="mentor-active-signals">
          {active.length === 0 ? <div className="p-6 text-center text-[#888] text-sm">Pa gen signal aktif.</div> :
            active.map((s) => (
              <div key={s.id} className="px-4 py-3 border-b border-[#161616] last:border-0 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-heading font-semibold text-sm">{s.asset} <span className={s.direction === "BUY" ? "text-[#22e07b]" : "text-[#ff5b5b]"}>{s.direction}</span></div>
                  <div className="font-mono-term text-[11px] text-[#888]">E {s.entry} · SL {s.sl} · TP1 {s.tp1} · RR {s.rr} · {s.mentor_name || s.posted_by}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button size="sm" onClick={() => close(s.id, "WIN")} data-testid={`close-win-${s.id}`} className="h-8 rounded-sm bg-[#22e07b]/15 text-[#22e07b] hover:bg-[#22e07b]/25 text-xs">WIN</Button>
                  <Button size="sm" onClick={() => close(s.id, "LOSS")} data-testid={`close-loss-${s.id}`} className="h-8 rounded-sm bg-[#ff5b5b]/15 text-[#ff5b5b] hover:bg-[#ff5b5b]/25 text-xs">LOSS</Button>
                  <Button size="sm" variant="ghost" onClick={() => close(s.id, "CANCEL")} data-testid={`close-cancel-${s.id}`} className="h-8 w-8 p-0 rounded-sm text-[#888] hover:text-white"><X className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
